import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown, faUser, faPen } from '@fortawesome/free-solid-svg-icons';

const ProfileHeader = ({ userData, isPremium, theme, onImageClick, onEditPress }) => {
  const initials = `${(userData.firstName || '')[0] || ''}${(userData.lastName || '')[0] || ''}`.toUpperCase();

  const styles = {
    container: {
      background: theme?.backgrounds?.card || '#fff',
      border: `1px solid ${theme?.borders?.primary || '#e5e7eb'}`,
      borderRadius: '1rem',
      padding: '1.25rem 1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      position: 'relative',
    },
    avatar: {
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      objectFit: 'cover',
      cursor: 'pointer',
      flexShrink: 0,
    },
    avatarPlaceholder: {
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      background: theme?.primary?.blue?.[100] || '#dbeafe',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
    },
    initials: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: theme?.primary?.blue?.[600] || '#2563eb',
    },
    details: {
      flex: 1,
      minWidth: 0,
    },
    name: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.text?.primary || '#111827',
      margin: 0,
    },
    email: {
      fontSize: '0.8125rem',
      color: theme?.text?.secondary || '#6b7280',
      margin: '0.125rem 0 0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    loginMethod: {
      fontSize: '0.75rem',
      color: theme?.text?.tertiary || '#9ca3af',
      margin: '0.25rem 0 0',
    },
    planBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.375rem',
      padding: '0.25rem 0.625rem',
      borderRadius: '1rem',
      fontSize: '0.6875rem',
      fontWeight: '600',
      marginTop: '0.5rem',
      background: isPremium
        ? (theme?.primary?.orange?.[50] || '#fff7ed')
        : (theme?.primary?.gray?.[50] || '#f9fafb'),
      color: isPremium
        ? (theme?.primary?.orange?.[600] || '#ea580c')
        : (theme?.primary?.gray?.[600] || '#4b5563'),
      border: `1px solid ${isPremium
        ? (theme?.primary?.orange?.[200] || '#fed7aa')
        : (theme?.primary?.gray?.[200] || '#e5e7eb')}`,
    },
    editButton: {
      width: '2rem',
      height: '2rem',
      borderRadius: '50%',
      background: theme?.backgrounds?.secondary || '#f3f4f6',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'background 0.15s ease',
    },
  };

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Avatar */}
      {userData.profilePic ? (
        <motion.img
          src={userData.profilePic}
          alt="Profile"
          style={styles.avatar}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onImageClick}
        />
      ) : (
        <motion.div
          style={styles.avatarPlaceholder}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onImageClick}
        >
          <span style={styles.initials}>{initials || '?'}</span>
        </motion.div>
      )}

      {/* Details */}
      <div style={styles.details}>
        <h3 style={styles.name}>
          {userData.firstName} {userData.lastName}
        </h3>
        <p style={styles.email}>{userData.email}</p>
        <p style={styles.loginMethod}>{userData.loginType}</p>
        <div style={styles.planBadge}>
          <FontAwesomeIcon
            icon={isPremium ? faCrown : faUser}
            style={{ fontSize: '0.625rem' }}
          />
          {isPremium ? 'Premium' : 'Free Plan'}
        </div>
      </div>

      {/* Edit button */}
      {onEditPress && (
        <button
          style={styles.editButton}
          onClick={onEditPress}
          onMouseEnter={e => e.currentTarget.style.background = theme?.backgrounds?.tertiary || '#e5e7eb'}
          onMouseLeave={e => e.currentTarget.style.background = theme?.backgrounds?.secondary || '#f3f4f6'}
          title="Edit Profile"
        >
          <FontAwesomeIcon
            icon={faPen}
            style={{ fontSize: '0.75rem', color: theme?.text?.secondary || '#6b7280' }}
          />
        </button>
      )}
    </motion.div>
  );
};

export default ProfileHeader;
